import React, { useEffect, useContext, useState } from "react";
import { FirebaseContext } from "../lib/firebase";
import { query, collection, onSnapshot } from "firebase/firestore";
import Modal from "react-modal";
import { Form, Field, Formik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { useRole } from "../lib/userRole";
import { useNavigate } from "react-router-dom";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "1px solid #eee",
    background: "#fff",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    outline: "none",
    padding: "0",
  },
  overlay: { zIndex: 5 },
};

const detailModalStyles = {
  content: {
    border: "1px solid #eee",
    background: "#fff",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    outline: "none",
    padding: "0",
  },
  overlay: { zIndex: 5 },
};

const schema = Yup.object().shape({
  file: Yup.mixed().required("Please choose a file"),
});

const folderSchema = Yup.object().shape({
  name: Yup.string().required("Please choose a name"),
});

export default function Folder({ data, first, forceRender }) {
  const navigate = useNavigate();
  const { role } = useRole();
  let folders = [];
  let docs = [];
  let fullDocs = [];
  let links = [];
  const hasParts = data?.path?.includes("folders");
  const parts = data?.path?.split("/");
  let result;
  let fullPath;
  let folderPath;
  if (parts?.length) {
    const shouldRun = parts[parts?.length - 1] !== data?.id;
    if (shouldRun) {
      result = parts?.filter((part) => part !== "folders").join("/");
      folderPath = `${data?.path}/folders/${data?.id}/`;
      fullPath = `${data?.path}/folders/${data?.id}/docs`;
    } else {
      result = "";
      folderPath = `${data?.path}`;
      fullPath = `${data?.path}/docs`;
    }
  } else {
    folderPath = "";
    fullPath = "folders/";
  }

  if (first) {
    folders = data.filter((item) => item.type === "folder");
    docs = data.filter((item) => item.type === "doc");
    links = data.filter((item) => item.type === "link");
  } else {
    docs = data?.docs;
    folders = data?.folders;
    fullDocs = docs.filter((item) => item.type !== "link");
    links = docs.filter((item) => item.type === "link");
  }
  const { api, database } = useContext(FirebaseContext);
  const [errors, setErrors] = useState([]);

  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [detailFile, setDetailFile] = useState({});
  const [detailLink, setDetailLink] = useState({});

  const [modalIsOpen, setIsOpen] = useState(false);
  const [linkModalIsOpen, setLinkModalIsOpen] = useState(false);
  const [folderModalIsOpen, setFolderModalIsOpen] = useState(false);
  const [detailModalIsOpen, setDetailModalIsOpen] = useState(false);
  const [linkDetailModalIsOpen, setLinkDetailModalIsOpen] = useState(false);
  const [confirmationIsOpen, setConfirmationIsOpen] = useState(false);
  const [folderConfirmationIsOpen, setFolderConfirmationIsOpen] =
    useState(false);
  const [linkConfirmationIsOpen, setLinkConfirmationIsOpen] = useState(false);

  function openModal() {
    setMessage("");
    setIsOpen(true);
  }

  function openLinkModal() {
    setLinkModalIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  function openFolderModal() {
    setFolderModalIsOpen(true);
  }

  function closeFolderModal() {
    setMessage("");
    setFolderModalIsOpen(false);
  }

  function openDetailModal() {
    setDetailModalIsOpen(true);
  }

  function closeDetailModal() {
    setDetailModalIsOpen(false);
  }

  function openLinkDetailModal() {
    setLinkDetailModalIsOpen(true);
  }

  function closeLinkDetailModal() {
    setLinkDetailModalIsOpen(false);
  }

  function toggleConfirmation() {
    setConfirmationIsOpen(!confirmationIsOpen);
  }

  function toggleLinkConfirmation() {
    setLinkConfirmationIsOpen(!linkConfirmationIsOpen);
  }

  function toggleFolderConfirmation() {
    setFolderConfirmationIsOpen(!folderConfirmationIsOpen);
  }

  async function handleDeleteError(id) {
    try {
      await api.removeError(id);
    } catch (error) {
      setErrorMessage(
        "An error occured when deleting the error. Please try again."
      );
    }
  }

  function handleView(doc) {
    setDetailFile(doc);
    openDetailModal();
  }

  function handleLinkView(doc) {
    setDetailLink(doc);
    openLinkDetailModal();
  }

  async function handleAdd(values) {
    let date = new Date().toUTCString().slice(5, 16);
    values.dateAdded = date;
    let timestamp = Date.now();
    values.timestamp = timestamp;
    values.type = "doc";
    let image;
    if (values.file) {
      image = values.file;
    }
    delete values.file;
    const terms = Object.values(values);

    values.searchTerms = terms;
    const response = await api.addFile(values, image, fullPath);
    if (response.success) {
      closeModal();
      forceRender();
    } else {
      setMessage("An error occured when adding the file. Please try again.");
    }
  }

  async function handleAddLink(values) {
    let date = new Date().toUTCString().slice(5, 16);
    values.dateAdded = date;
    let timestamp = Date.now();
    values.timestamp = timestamp;
    values.type = "link";

    const terms = Object.values(values);

    values.searchTerms = terms;
    const response = await api.addLink(values, fullPath);
    if (response.success) {
      closeModal();
      forceRender();
    } else {
      setMessage("An error occured when adding the link. Please try again.");
    }
  }

  async function handleAddFolder(values) {
    let date = new Date().toUTCString().slice(5, 16);
    values.dateAdded = date;
    let timestamp = Date.now();
    values.timestamp = timestamp;
    values.type = "folder";
    const terms = Object.values(values);

    values.searchTerms = terms;
    const response = await api.addFolder(values, folderPath);
    if (response.success) {
      closeFolderModal();
      forceRender();
    } else {
      setMessage("An error occured when adding the file. Please try again.");
    }
  }

  async function handleDeleteLink(id, path) {
    try {
      await api.removeLink(id, path);
      setLinkDetailModalIsOpen(false);
      setDetailLink({});
      toggleLinkConfirmation();
      forceRender();
    } catch (error) {
      setLinkDetailModalIsOpen(false);
      setDetailLink({});
      setMessage("An error occured when deleting the file. Please try again.");
    }
  }

  async function handleDelete(id, path) {
    try {
      await api.removeFile(id, path);
      setDetailModalIsOpen(false);
      setDetailFile({});
      toggleConfirmation();
      forceRender();
    } catch (error) {
      setDetailModalIsOpen(false);
      setDetailFile({});
      setMessage("An error occured when deleting the file. Please try again.");
    }
  }

  async function handleDeleteFolder() {
    try {
      await api.removeFolder(folderPath);
      toggleFolderConfirmation();
      forceRender();
      navigate(`/dashboard/${result}`);
    } catch (error) {
      setMessage("An error occured when deleting the file. Please try again.");
    }
  }

  async function handleDownload(url, name) {
    const xhr = new XMLHttpRequest();
    xhr.responseType = "blob";
    xhr.onload = (event) => {
      const blob = xhr.response;

      // Create a temporary link element
      const link = document.createElement("a");

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;

      // Optionally set a filename for the downloaded file
      link.download = name;
      // Append the link to the document and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the Object URL and removing the link
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    };

    xhr.open("GET", url);
    xhr.send();
  }

  useEffect(() => {
    const q = query(collection(database, "errors"));

    const unsubscribe = onSnapshot(q, (QuerySnapshot) => {
      const fetchedErrors = [];
      QuerySnapshot.forEach((doc) => {
        fetchedErrors.push({ ...doc.data(), id: doc.id });
      });
      const sortedErrors = fetchedErrors.sort(
        (a, b) => a.createdAt - b.createdAt
      );
      setErrors(sortedErrors);
    });

    return () => unsubscribe;
  }, [database]);

  return (
    <>
      <div className='errors fixed top-10 right-0 mr-10'>
        {errorMessage && (
          <div className='h-10 w-full z-10 mb-8'>
            <div className='text-white px-6 py-4 border-0 rounded relative bg-red-500'>
              <span className='text-xl inline-block mr-5 align-middle'>
                <i className='fas fa-bell' />
              </span>
              <span className='inline-block align-middle mr-8'>
                <b className='capitalize'>Oh Shoot!</b> {errorMessage}
              </span>
              <button
                onClick={() => setErrorMessage("")}
                className='absolute bg-transparent text-2xl font-semibold leading-none right-0 top-0 mt-4 mr-6 outline-none focus:outline-none'
              >
                <span>×</span>
              </button>
            </div>
          </div>
        )}
        {errors.map((error) => (
          <div className='h-10 w-full z-10 mb-8'>
            <div className='text-white px-6 py-4 border-0 rounded relative bg-red-300 flex flex-col'>
              <div>
                <span className='text-xl inline-block mr-5 align-middle'>
                  <i className='fas fa-bell' />
                </span>
                <span className='inline-block align-middle mr-8'>
                  <b className='capitalize'>Oh Shoot!</b> {error.details}
                </span>

                <button
                  onClick={() => handleDeleteError(error.id)}
                  className='absolute bg-transparent text-2xl font-semibold leading-none right-0 top-0 mt-4 mr-6 outline-none focus:outline-none'
                >
                  <span>×</span>
                </button>
              </div>
              {error.errors && (
                <span className='text-[10px] inline-block mr-5 align-middle'>
                  {JSON.stringify(error.errors)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className='px-4 sm:px-6 lg:px-8 mt-10'>
        <div className='mb-10'>
          {hasParts ? (
            <Link
              to={`/dashboard/${result}`}
              className='rounded-md max-w-20 mb-10 bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
            >
              Back
            </Link>
          ) : (
            <div></div>
          )}
        </div>
        <div className='sm:flex sm:items-center mb-24'>
          <div className='flex justify-center lg:flex-auto lg:justify-start'>
            <h1 className='text-2xl font-[700] leading-loose md:text-3xl text-gray-900 sr-only'>
              Farm Equipment USA Internal
            </h1>
            <img src='/logo.png' alt='FEUSA logo'></img>
          </div>

          <div className='mt-4 sm:ml-16 sm:mt-0 flex flex-col justify-end'>
            {role.role === "admin" && (
              <>
                <Link
                  to='/register'
                  className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-5'
                >
                  Add New User
                </Link>
                <button
                  onClick={() => openFolderModal()}
                  type='button'
                  className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-5'
                >
                  Add Folder
                </button>
                {hasParts && (
                  <>
                    <button
                      onClick={() => openModal()}
                      type='button'
                      className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-5'
                    >
                      Add file
                    </button>
                    <button
                      onClick={() => openLinkModal()}
                      type='button'
                      className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-5'
                    >
                      Add link
                    </button>
                    <button
                      onClick={() => toggleFolderConfirmation()}
                      type='button'
                      className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-5'
                    >
                      {`Delete ${data.name} folder`}
                    </button>
                  </>
                )}
              </>
            )}
            <button
              onClick={() => navigate("/dashboard/employees")}
              type='button'
              className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-4'
            >
              Employee Directory
            </button>
            <button
              className="className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'"
              onClick={() => {
                api.logout();
                navigate("/");
              }}
            >
              Logout
            </button>
          </div>
        </div>

        <div className='-mx-4 mt-8 sm:-mx-0 px-5'>
          <div className='text-2xl mb-5'>{data.name}</div>
          <section className='w-full'>
            <div className='w-full border-b-2'>
              <div className='font-black text-gray-900'>Folders</div>
            </div>
            {first && folders.length === 0 && (
              <div className='flex flex-row justify-center items-center mt-20'>
                <span className='relative flex h-3 w-3 ml-3'>
                  <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75'></span>
                  <span className='relative inline-flex rounded-full h-3 w-3 bg-red'></span>
                </span>
              </div>
            )}
            <div className='w-full mt-5 grid gap-10 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'>
              {folders?.map((folder) => {
                return (
                  <Link to={folder.id} key={folder.id}>
                    <div className='flex flex-col justify-center items-center'>
                      <img src='/folders.png' alt='folder'></img>
                      {/* <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth='0.5'
                        stroke='currentColor'
                        className='w-20 h-20'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z'
                        />
                      </svg> */}

                      <div className='text-center text-xs'>{folder.name}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
          {hasParts && (
            <>
              <section className='w-full mt-10'>
                <div className='w-full border-b-2'>
                  <div className='font-black text-gray-900'>Documents</div>
                </div>

                <div className='w-full mt-5 grid gap-10 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'>
                  {fullDocs?.map((folder) => (
                    <div
                      key={folder.id}
                      className='flex flex-col justify-center items-center'
                    >
                      <div
                        onClick={() => handleView(folder)}
                        className='flex flex-col justify-center items-center hover:cursor-pointer'
                      >
                        <img src='/file.png' alt='file'></img>
                        {/* <svg
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={0.5}
                          stroke='currentColor'
                          className='w-20 h-20'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z'
                          />
                        </svg> */}
                      </div>
                      <a
                        href={folder.url}
                        rel='noopener noreferrer'
                        target='_blank'
                        key={folder.id}
                      >
                        <div className='text-center text-xs'>{folder.name}</div>
                      </a>
                      <button
                        className='text-red-400 border-[1px] mt-2 text-xs border-red-400 rounded-md px-2 py-1 hover:text-rose-700'
                        onClick={() => handleDownload(folder.url, folder.name)}
                        // target='_blank'
                        // rel='noopener noreferrer'
                        // download
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
          {hasParts && (
            <>
              <section className='w-full mt-10'>
                <div className='w-full border-b-2'>
                  <div className='font-black text-gray-900'>Links</div>
                </div>

                <div className='w-full mt-5 grid gap-10 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'>
                  {links?.map((folder) => (
                    <div
                      key={folder.id}
                      className='flex flex-col justify-center items-center'
                    >
                      <div
                        onClick={() => handleLinkView(folder)}
                        className='flex flex-col justify-center items-center hover:cursor-pointer'
                      >
                        <img src='/file.png' alt='file'></img>
                        {/* <svg
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={0.5}
                          stroke='currentColor'
                          className='w-20 h-20'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z'
                          />
                        </svg> */}
                      </div>
                      <a
                        href={folder.link}
                        rel='noopener noreferrer'
                        target='_blank'
                        key={folder.link}
                      >
                        <div className='text-center text-xs'>{folder.name}</div>
                      </a>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        style={modalStyles}
        contentLabel='Add Modal'
      >
        <div className='bg-zinc-50 shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Add a file
              </h3>
              <button
                onClick={closeModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <Formik
              initialValues={{
                file: "",
              }}
              validationSchema={schema}
              onSubmit={async (values, actions) => {
                await handleAdd(values);
                actions.setSubmitting(false);
              }}
            >
              {({ isSubmitting, setFieldValue, errors, touched, values }) => (
                <Form className='mt-5 sm:flex sm:items-center flex-col'>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='file'
                      className='block text-sm font-semibold leading-6 mb-5'
                    >
                      File
                    </label>
                    <input
                      id='file'
                      name='file'
                      type='file'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      onChange={(event) => {
                        setFieldValue("file", event.currentTarget.files[0]);
                      }}
                    />

                    {errors.file && touched.file ? (
                      <div className='error'>{errors.file}</div>
                    ) : null}
                  </div>
                  <button
                    type='submit'
                    className='block rounded-md bg-red-600 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
                  >
                    {isSubmitting ? (
                      <div className='flex flex-row justify-center items-center'>
                        Pending
                        <span className='relative flex h-3 w-3 ml-3'>
                          <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-200 opacity-75'></span>
                          <span className='relative inline-flex rounded-full h-3 w-3 bg-white'></span>
                        </span>
                      </div>
                    ) : (
                      `Save`
                    )}
                  </button>
                  {message && (
                    <p className='text-sm mt-2 text-rose-600'>{message}</p>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={linkModalIsOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeFolderModal}
        style={modalStyles}
        contentLabel='Edit Modal'
      >
        <div className='bg-zinc-50 shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Add Link
              </h3>
              <button
                onClick={closeFolderModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <Formik
              initialValues={{
                name: "",
                link: "",
              }}
              onSubmit={async (values, actions) => {
                await handleAddLink(values);
                actions.setSubmitting(false);
              }}
            >
              {({ isSubmitting, errors, touched, values }) => (
                <Form className='mt-5 sm:flex sm:items-center flex-col'>
                  <div className='w-full sm:max-w-xs mr-2'>
                    <label
                      htmlFor='link-name'
                      className='block text-sm font-semibold leading-6'
                    >
                      Name
                    </label>
                    <Field
                      type='text'
                      name='name'
                      id='link-name'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.name && touched.name ? (
                      <div className='error'>{errors.name}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs mr-2'>
                    <label
                      htmlFor='link'
                      className='block text-sm font-semibold leading-6'
                    >
                      Link
                    </label>
                    <Field
                      type='text'
                      name='link'
                      id='link'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.link && touched.link ? (
                      <div className='error'>{errors.link}</div>
                    ) : null}
                  </div>

                  <button
                    type='submit'
                    className='block rounded-md bg-red-600 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
                  >
                    {isSubmitting ? (
                      <span className='relative flex h-3 w-3'>
                        <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75'></span>
                        <span className='relative inline-flex rounded-full h-3 w-3 bg-sky-500'></span>
                      </span>
                    ) : (
                      `Save`
                    )}
                  </button>
                  {message && (
                    <p className='text-sm mt-2 text-rose-600'>{message}</p>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={folderModalIsOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeFolderModal}
        style={modalStyles}
        contentLabel='Edit Modal'
      >
        <div className='bg-zinc-50 shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Add Folder
              </h3>
              <button
                onClick={closeFolderModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <Formik
              initialValues={{
                name: "",
              }}
              validationSchema={folderSchema}
              onSubmit={async (values, actions) => {
                await handleAddFolder(values);
                actions.setSubmitting(false);
              }}
            >
              {({ isSubmitting, errors, touched, values }) => (
                <Form className='mt-5 sm:flex sm:items-center flex-col'>
                  <div className='w-full sm:max-w-xs mr-2'>
                    <label
                      htmlFor='name'
                      className='block text-sm font-semibold leading-6'
                    >
                      Name
                    </label>
                    <Field
                      type='text'
                      name='name'
                      id='name'
                      autoComplete='given-name'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.name && touched.name ? (
                      <div className='error'>{errors.name}</div>
                    ) : null}
                  </div>

                  <button
                    type='submit'
                    className='block rounded-md bg-red-600 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
                  >
                    {isSubmitting ? (
                      <span className='relative flex h-3 w-3'>
                        <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75'></span>
                        <span className='relative inline-flex rounded-full h-3 w-3 bg-sky-500'></span>
                      </span>
                    ) : (
                      `Save`
                    )}
                  </button>
                  {message && (
                    <p className='text-sm mt-2 text-rose-600'>{message}</p>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={detailModalIsOpen}
        onRequestClose={closeDetailModal}
        style={detailModalStyles}
        contentLabel='Detail Modal'
      >
        <div className='bg-zinc-50 h-full shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                {detailFile.name}
              </h3>
              <button
                onClick={closeDetailModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Close
              </button>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
              {detailFile.url && (
                <div className='w-full sm:max-w-xs'>
                  {detailFile.url.includes(".pdf") ||
                  detailFile.url.includes(".docx") ||
                  detailFile.url.includes(".xlsx") ? (
                    <a
                      className='text-rose-800 font-semibold hover:text-rose-700'
                      href={detailFile.url}
                      target='_blank'
                      rel='noreferrer noopener'
                    >
                      GET FILE
                    </a>
                  ) : (
                    <>
                      <img
                        className='mb-5'
                        alt='license'
                        src={detailFile.url}
                      />
                      <button
                        className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1 hover:text-rose-700'
                        onClick={() =>
                          handleDownload(detailFile.url, detailFile.name)
                        }
                      >
                        Download
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => toggleConfirmation()}
              className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1 mt-5'
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={linkDetailModalIsOpen}
        onRequestClose={closeLinkDetailModal}
        style={detailModalStyles}
        contentLabel='Link Detail Modal'
      >
        <div className='bg-zinc-50 h-full shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                {detailLink.name}
              </h3>
              <button
                onClick={closeLinkDetailModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Close
              </button>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
              <div className='w-full sm:max-w-xs'>
                <a
                  className='text-rose-800 font-semibold hover:text-rose-700'
                  href={detailLink.link}
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  {detailLink.link}
                </a>
              </div>
            </div>
            <button
              onClick={() => toggleLinkConfirmation()}
              className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1 mt-5'
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={confirmationIsOpen}
        onRequestClose={toggleConfirmation}
        style={modalStyles}
        contentLabel='Confirmation Modal'
      >
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Delete File
              </h3>
              <button
                onClick={toggleConfirmation}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <div className='mt-2 mb-4 max-w-xl text-sm text-gray-500'>
              <p>Are you sure you want to delete this file?</p>
            </div>

            <button
              onClick={(e) => handleDelete(detailFile.id, fullPath)}
              className='block rounded-md bg-red-600 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={linkConfirmationIsOpen}
        onRequestClose={toggleLinkConfirmation}
        style={modalStyles}
        contentLabel='Confirmation Modal'
      >
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Delete Link
              </h3>
              <button
                onClick={toggleLinkConfirmation}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <div className='mt-2 mb-4 max-w-xl text-sm text-gray-500'>
              <p>Are you sure you want to delete this link?</p>
            </div>

            <button
              onClick={(e) => handleDeleteLink(detailLink.id, fullPath)}
              className='block rounded-md bg-red-600 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={folderConfirmationIsOpen}
        onRequestClose={toggleFolderConfirmation}
        style={modalStyles}
        contentLabel='Folder Confirmation Modal'
      >
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Delete Folder
              </h3>
              <button
                onClick={toggleFolderConfirmation}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <div className='mt-2 mb-4 max-w-xl text-sm text-gray-500'>
              <p>
                Are you sure you want to delete this folder and all of its
                contents?
              </p>
            </div>

            <button
              onClick={(e) => handleDeleteFolder()}
              className='block rounded-md bg-red-600 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
